import {
  Alert,
  Button,
  Checkbox,
  Col,
  Layout,
  Row,
  Spin,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createDiscordUrl } from "../../utils/constants";
import { DiscordButton } from "../DiscordPickGuild/DiscordButton";
import { CheckOutlined } from "@ant-design/icons";
import { getSupaClient, getWorkspace } from "../../lib/supabase";

import DotsFishLoading from "../DotsFishLoading/DotsFishLoading";
import "./DiscordBotAdded.css";
import { setOnboardingStatus } from "../Onboarding/Onboarding";
import supabase from "../../lib/supabase";
import { useMutation, useQuery } from "react-query";
import { fetchAllTeams } from "../../App";
import TagsMsgs from "apps/dots-frontend/types/tags_msgs";

const { Title, Text } = Typography;

const MissingGuildId = () => <div>Missing guild id</div>;

const InsufficientPermissions = ({ guildId }: { guildId: string }) => (
  <div>
    <Title>Insufficient permissions</Title>
    <Text>
      Authorize with your Discord and check all the permission boxes so we can
      install the app on your server
    </Text>
    <div style={{ marginTop: "2rem" }}>
      <a href={createDiscordUrl("add-bot", guildId)}>
        <DiscordButton>Authorize</DiscordButton>
      </a>
    </div>
  </div>
);

/**
 * 2908TODO documentation
 * @param permissions
 * @returns
 */
function hasRequiredPermissions(permissions: string | null) {
  if (permissions === null) return false;
  try {
    // Discord permissions: https://discord.com/developers/docs/topics/permissions
    const required = [
      0x0000000000000400, //VIEW_CHANNEL (Read Messages)
      0x0000000000000800, //SEND_MESSAGES
      0x0000000000000010, //MANAGE_CHANNELS
    ];
    const perms = parseInt(permissions);
    return required.every((p) => (perms & p) !== 0);
  } catch (e) {
    console.error("Invalid permission", permissions);
  }
  return false;
}

function useAddNewGuildMutation() {
  return useMutation<any, Error, string>(async (guildId: string) => {
    return await addNewGuild(guildId);
  });
}

function useStartETLMutation() {
  return useMutation<any, Error, string>(async (guildId: string) => {
    return await startETL(guildId);
  });
}

type TestTriggerFlowParams = {
  guildId: string;
  channelId: string;
  workspaceId: string;
  flowName: "private-channel-flow" | "ephemeral-test-flow";
};

function useCreateTestFlowMutation() {
  return useMutation<any, Error, TestTriggerFlowParams>(
    async (testTriggerFlowParams) => {
      return await createTestFlow(testTriggerFlowParams);
    }
  );
}

const GuildTodosForUser = ({ onAllSet }: { onAllSet: () => void }) => {
  const [rolesApplied, setRolesApplied] = useState(false);
  const [roleMovedToTop, setRoleMovedToTop] = useState(false);
  return (
    <div>
      <h1 style={{ fontSize: "1.5rem", marginBottom: "1.5rem" }}>
        Two more quick things before we can set up a test flow on your server!
      </h1>
      <Row>
        <Col span={12}>
          <Checkbox
            checked={rolesApplied}
            onChange={(e) => setRolesApplied(e.target.checked)}
          >
            Apply the verified (or equivalent) role in your server for Dots to
            see the public channels!
          </Checkbox>
          <div style={{ marginLeft: "24px", marginTop: "0.5rem" }}>
            <Text type="secondary">
              How? You can do this by finding the Dots bot in your server,
              adding roles by clicking on it and applying the relevant role(s).
              More info here.
            </Text>
          </div>
        </Col>
        <Col span={12}>
          <img className="demo-image" src="/discord_bot_role.png" width="295" />
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Checkbox
            checked={roleMovedToTop}
            onChange={(e) => setRoleMovedToTop(e.target.checked)}
          >
            Move the Dots bot above certain roles that you would want Dots to
            apply to new members
          </Checkbox>
          <div style={{ marginLeft: "24px", marginTop: "0.5rem" }}>
            <Text type="secondary">
              How? Navigate to Server Settings (top right), and find Roles
              section. Here find the Dots role and scroll it up. More info here
            </Text>
          </div>
        </Col>
        <Col span={12}>
          <img
            className="demo-image"
            src="/discord_bot_role_hierarchy.png"
            width="443"
          />
        </Col>
      </Row>
      <Button
        style={{ marginTop: "2rem" }}
        type="primary"
        size="large"
        onClick={onAllSet}
        disabled={!rolesApplied || !roleMovedToTop}
      >
        All set!
      </Button>
    </div>
  );
};
/**
 * 2908TODO documentation
 * @param guildId
 * @returns
 */
async function addNewGuild(guildId: string) {
  const supabase = getSupaClient();
  const params = new URLSearchParams(location.search);
  const code = params.get("code") ?? "";
  const path = `/guilds/${guildId}`;
  const fullURL = `${process.env["REACT_APP_API_URL"]}${path}?`;
  const authToken = supabase.auth.session()?.access_token;
  const redirectUri = encodeURIComponent(
    `${process.env["REACT_APP_URI"]}/onboarding/discord/bot-added/`
  );
  const response = await fetch(
    fullURL +
      new URLSearchParams({
        code,
        redirectUri,
      }),
    {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );
  if (response.ok) {
    const responseJson = await response.json();
    await supabase.auth.refreshSession();
    return {
      channelId: responseJson.channelId,
      workspaceId: responseJson.workspaceId,
    };
  } else {
    let reason = await response.text();
    throw new Error(reason);
  }
}

/**
 * 2908TODO documentation
 * @returns
 * @param testTriggerFlowParams
 */
async function createTestFlow(testTriggerFlowParams: TestTriggerFlowParams) {
  const { guildId, channelId, workspaceId, flowName } = testTriggerFlowParams;

  const path = `/guilds/${guildId}/test-flow`;
  const fullURL = `${process.env["REACT_APP_API_URL"]}${path}?`;
  const authToken = getSupaClient().auth.session()?.access_token;
  const response = await fetch(fullURL, {
    method: "POST",
    body: JSON.stringify({
      channelId,
      workspaceId,
      flowName,
      authorUserId: localStorage.getItem("discord-id") || "",
    }),
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (response.ok) {
    const responseJson = await response.json();
    return { channelId: responseJson.channelId };
  } else {
    let reason = await response.text();
    throw new Error(reason);
  }
}

/**
 * 2908TODO documentation
 * @param guildId
 * @returns
 */
async function startETL(guildId: string) {
  const path = `/guilds/${guildId}/start`;
  const fullURL = `${process.env["REACT_APP_API_URL"]}${path}?`;
  const authToken = getSupaClient().auth.session()?.access_token;
  const response = await fetch(fullURL, {
    method: "POST",
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (response.ok) {
    const responseJson = await response.json();
    return { channelId: responseJson.channelId, flows: responseJson.flows };
  } else {
    let reason = await response.text();
    throw new Error(reason);
  }
}

/**
 * 2908TODO documentation
 * @returns
 */
const GuildAdditionalSetupProgress = () => {
  const steps = [
    "Creating a test channel",
    "Pulling basic discord server data",
    "Getting it ready!",
  ];
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      if (progress < steps.length) {
        setProgress((c) => c + 0.5);
      } else {
        clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (progress >= steps.length) {
      navigate(`/onboarding/discord/all-set`);
    }
  }, [progress]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <div>
        <DotsFishLoading />
        <h1 style={{ fontSize: "1.5rem", marginBottom: "1.5rem" }}>
          Great! 30 more seconds
        </h1>
        {steps.slice(0, Math.ceil(progress + 1)).map((s, index) => (
          <div className="flex gap-3 items-center" key={s}>
            {progress <= index ? (
              <Spin size="small" />
            ) : (
              <CheckOutlined style={{ color: "#25B391" }} />
            )}
            <span>{s}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const { Content } = Layout;

export function useDotsAdminsChannelUrl() {
  const guildId = useOnboardingGuildId();
  const channelIdResult = useDotsAdminsChannelId();
  return {
    ...channelIdResult,
    data: channelIdResult.data
      ? `https://discord.com/channels/${guildId}/${channelIdResult.data}`
      : undefined,
  };
}

function useDotsAdminsChannelId() {
  // check local storage for guild id
  // see if dots-admins-channel is populate in supabase
  const guildId = useOnboardingGuildId();
  const result = useQuery(
    ["onboarding-dots-admins-channel", guildId],
    async () => {
      const value = await getSupaClient()
        .from("discord_guilds")
        .select("dots_admins_channel")
        .eq("discord_id", guildId);
      console.log("value", value);
      return value.data?.[0]?.dots_admins_channel as string;
    }
  );
  return result;
}

function useGuild() {
  const guildId = useOnboardingGuildId();
  const result = useQuery(["onboarding-guild", guildId], async () => {
    const value = await getSupaClient()
      .from("discord_guilds")
      .select("discord_id, name, is_active")
      .eq("discord_id", guildId);
    console.log("guild ", value);
    return value.data;
  });
  return result;
}

function useHasGuildBeenInitialized() {
  const channelResult = useDotsAdminsChannelId();
  return { ...channelResult, data: Boolean(channelResult.data) };
}

function useOnboardingGuildId() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const guildIdInURL = params.get("guild_id");
  const key = `onboarding-guild-id`;
  if (guildIdInURL) {
    localStorage.setItem(key, guildIdInURL);
  }
  return guildIdInURL ?? localStorage.getItem(key);
}

/**
 *
 * Component to show when a user has authorized to add Dots bot to one of their servers. If there are insufficient permissions, we show them an appropriate message.
 */
const DiscordBotAdded = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasGuildBeenInitialized = useHasGuildBeenInitialized();
  const guildId = useOnboardingGuildId();
  const permissions = params.get("permissions");
  const user = supabase.auth.user();
  const workspace = getWorkspace(getSupaClient());
  const addGuildMutation = useAddNewGuildMutation();
  const startETLMutation = useStartETLMutation();
  // const { mutateAsync } = useCreateTestFlowMutation();
  const adminChannelIdMutation = useDotsAdminsChannelId();
  const dotsGuild = useGuild();
  const navigate = useNavigate();

  //We can get the dots admin channel id in two ways
  // 1 - when the user adds the bot the useAddNewGuildMutation hook will return the dots admin channel id
  // 2 - If the user has already been added to the guild, we can get the dots admin channel id from the supabase table
  const [adminChannelId, setAdminChannelId] = useState<string>("");
  useEffect(() => {
    if (guildId) {
      addGuildMutation.mutate(guildId, {
        onSuccess: () => {
          if (user?.email) {
            setOnboardingStatus(user.email, "discord-bot-added");
            window.analytics.track("Sign up - discord bot added");
          }
        },
      });
    }
  }, []);

  const [todosDone, setTodosDone] = useState(false);

  useEffect(() => {
    if (adminChannelIdMutation.data) {
      setAdminChannelId(adminChannelIdMutation.data);
    }
  }, [adminChannelIdMutation.isFetched]);

  let content = !todosDone ? (
    <GuildTodosForUser
      onAllSet={async () => {
        if (user?.email) {
          setOnboardingStatus(user?.email, "discord-all-set");
          window.analytics.track("Sign up - discord final step");
        }
        setTodosDone(true);
        startETLMutation.mutate(guildId ?? "");

        //create basic tags for saved messages, only if the guild doesn't have any tags yet
        const { data } = await supabase
          .from<TagsMsgs>("tags_msgs")
          .select("*")
          .match({ guild: guildId });

        if (data && data.length === 0) {
          await supabase.from("tags_msgs").insert([
            { name: "Feedback", guild: guildId, color: "magenta", workspace },
            { name: "Support", guild: guildId, color: "red", workspace },
            { name: "Ideas", guild: guildId, color: "volcano", workspace },
          ]);
        }

        // Add new guild to list of teams and set it as the active workspace
        const session = supabase.auth.session();

        const { userSlackTeams, userDiscordTeams } = await fetchAllTeams(
          supabase,
          session
        );

        if (userDiscordTeams.data && guildId) {
          const userTeams = (userSlackTeams.data || []).concat(
            userDiscordTeams.data
          );
          const [newTeam] = userDiscordTeams.data.filter((item: any) => {
            return item.guild.discord_id === guildId;
          });

          if (newTeam) {
            localStorage.setItem("platform", "discord");
            localStorage.setItem("guild-id", newTeam.guild.discord_id);
            localStorage.setItem("discord-id", newTeam.discord_id);
            localStorage.setItem("guild-name", newTeam.guild.name);
            // remove slack related
            localStorage.removeItem("team-id");
            localStorage.removeItem("team-name");
            localStorage.removeItem("slack-id");
          }
          localStorage.setItem("teams", JSON.stringify(userTeams));
        }

        //create test flow 1
        // commented for now - testing new onboarding strategy

        // mutateAsync({
        //   guildId: guildId as string,
        //   channelId: adminChannelId,
        //   flowName: "private-channel-flow",
        //   workspaceId: getWorkspace(getSupaClient()),
        // });
      }}
    />
  ) : (
    <GuildAdditionalSetupProgress />
  );
  // If the guild hasn't been initialized, we validate `guild_id` and `permissions` in the URL. Otherwise, we'll just show GuildInitialSetup/GuildTodos
  if (hasGuildBeenInitialized.isFetched && !hasGuildBeenInitialized.data) {
    if (!guildId) content = <MissingGuildId />;
    else if (!hasRequiredPermissions(permissions)) {
      content = <InsufficientPermissions guildId={guildId} />;
    }
  }

  const loadingView = (
    <div>
      <Row justify="center">
        <DotsFishLoading />
      </Row>
      <Row justify="center">
        Setting up your community info... (please don't close this tab)
      </Row>
    </div>
  );
  return (
    <Content style={{ minHeight: "100vh" }} className="discord-all-set-bg">
      <Row justify="center" align="middle" style={{ minHeight: "100vh" }}>
        <Col span={16}>
          <div
            className="rounded-lg p-14 discord-all-set-card flex items-center justify-center"
            style={{ minHeight: "90vh" }}
          >
            {/* no errors, no loading and no data present */}
            {!hasGuildBeenInitialized.isFetched &&
              !addGuildMutation.error &&
              !startETLMutation.error &&
              !addGuildMutation.isLoading &&
              !startETLMutation.isLoading &&
              loadingView}

            {/* actively loading mutations */}
            {(addGuildMutation.isLoading || startETLMutation.isLoading) &&
              loadingView}

            {/*error present */}
            {addGuildMutation.error && (
              <Alert
                showIcon
                message={`Failed to add guild ${guildId}`}
                description={
                  addGuildMutation.error.message.includes(
                    "Currently we don't support switching accounts."
                  ) ? (
                    <>
                      {addGuildMutation.error.message}
                      <div
                        className="hover:underline"
                        onClick={() => {
                          navigate("/signin");
                        }}
                      >
                        Click here to sign in{" "}
                      </div>
                    </>
                  ) : (
                    addGuildMutation.error.message
                  )
                }
              />
            )}

            {/* error present */}
            {startETLMutation.error && (
              <Alert
                showIcon
                message={`Failed to add guild ${guildId}`}
                description={startETLMutation.error.message}
              />
            )}

            {/* no errors, no loading and data present */}
            {hasGuildBeenInitialized.isFetched &&
              !addGuildMutation.error &&
              !startETLMutation.error &&
              !addGuildMutation.isLoading &&
              !startETLMutation.isLoading &&
              content}
          </div>
        </Col>
      </Row>
    </Content>
  );
};

export default DiscordBotAdded;
